import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, Sparkles, TrendingUp, RefreshCw, Flame, Zap,
  Play, FileText, Target, Calendar, Eye, Music, MapPin,
  Award, Clock, Video, Hash, Users, BarChart3, Star,
} from "lucide-react";

const SUB_TABS = [
  { key: "report", label: "Báo cáo", icon: BarChart3 },
  { key: "all", label: "Tất cả", icon: Flame },
  { key: "quick", label: "Quick Win", icon: Zap },
  { key: "pickleball", label: "Pickleball", icon: Target },
  { key: "gym", label: "Gym", icon: TrendingUp },
  { key: "cafe", label: "Cafe", icon: Star },
];

const CAT_LABELS = {
  pickleball: "Pickleball",
  gym_fitness: "Gym / Fitness",
  cafe_drinks: "Cafe / Do uong",
  lifestyle_genz: "Lifestyle",
  family_kids: "Gia dinh",
  review_experience: "Review",
  comedy_entertainment: "Hai huoc",
  before_after: "Before-After",
};

const CAT_COLORS = {
  pickleball: "bg-lime-500/20 text-lime-400",
  gym_fitness: "bg-cyan-500/20 text-cyan-400",
  cafe_drinks: "bg-amber-500/20 text-amber-400",
  lifestyle_genz: "bg-purple-500/20 text-purple-400",
  family_kids: "bg-pink-500/20 text-pink-400",
  review_experience: "bg-sky-500/20 text-sky-400",
  comedy_entertainment: "bg-orange-500/20 text-orange-400",
  before_after: "bg-emerald-500/20 text-emerald-400",
};

export default function TikTokTrendsPanel() {
  const [subTab, setSubTab] = useState("report");
  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="tiktok-panel">
      <div className="flex gap-1 px-4 py-2 border-b border-[#1e293b] bg-[#0a0f1a] overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${subTab === t.key ? "bg-[#1e293b] text-rose-400" : "text-slate-500 hover:text-slate-300"}`}
            data-testid={`tiktok-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {subTab === "report" && <DailyReportTab />}
        {subTab === "all" && <AllTrendsTab />}
        {subTab === "quick" && <QuickWinTab />}
        {subTab === "pickleball" && <CategoryTrendsTab category="pickleball" />}
        {subTab === "gym" && <CategoryTrendsTab category="gym_fitness" />}
        {subTab === "cafe" && <CategoryTrendsTab category="cafe_drinks" />}
      </div>
    </div>
  );
}

// ==================== DAILY REPORT ====================
function DailyReportTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get("/tiktok-trends/daily-report").then(r => { setReport(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/tiktok-trends/generate");
      toast.success(`AI da phan tich ${data.count} trends!`);
      // Refresh report
      const r = await api.post("/tiktok-trends/daily-report/refresh");
      setReport(r.data);
    } catch { toast.error("Lỗi"); }
    finally { setGenerating(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/tiktok-trends/daily-report/refresh");
      setReport(data);
    } catch { toast.error("Lỗi"); }
    finally { setRefreshing(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-rose-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="tiktok-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Flame className="w-5 h-5 text-rose-400" /> TikTok Trends Viet Nam</h2>
          <p className="text-[10px] text-slate-500 mt-0.5">Phân tích xu hướng TikTok cho KARINOX Arena</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={refresh} disabled={refreshing} variant="outline" size="sm" className="border-[#334155] text-slate-300 gap-1.5 text-xs hover:text-rose-400">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Làm mới
          </Button>
          <Button onClick={generate} disabled={generating} className="bg-rose-500 hover:bg-rose-400 text-white gap-1.5 text-xs" data-testid="generate-trends-btn">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            AI Phân tích
          </Button>
        </div>
      </div>

      {/* API Status */}
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        <span className="text-[10px] text-amber-400">Nguồn: AI Analysis (TikTok API chưa kết nối — dữ liệu từ AI market knowledge)</span>
      </div>

      {report?.status === "no_data" ? (
        <div className="text-center py-12">
          <Flame className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{report.message}</p>
          <Button onClick={generate} disabled={generating} className="mt-4 bg-rose-500 hover:bg-rose-400 text-white gap-1.5" data-testid="first-generate-btn">
            <Sparkles className="w-4 h-4" /> AI Phân tích xu hướng TikTok VN
          </Button>
        </div>
      ) : report?.sections ? (
        <div className="space-y-4">
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500 uppercase">Tổng trends</p>
              <p className="text-xl font-bold text-rose-400">{report.total_trends}</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500 uppercase">Điểm TB</p>
              <p className="text-xl font-bold text-cyan-400">{report.avg_score}</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500 uppercase">Quick Win</p>
              <p className="text-xl font-bold text-lime-400">{report.sections.quick_wins?.length || 0}</p>
            </div>
          </div>

          {/* Sections */}
          <ReportSection title="Trend nổi bật hôm nay" icon={<Flame className="w-3.5 h-3.5 text-rose-400" />} items={report.sections.top_today} />
          <ReportSection title="Quick Win — Dễ làm ngay" icon={<Zap className="w-3.5 h-3.5 text-lime-400" />} items={report.sections.quick_wins} />
          <ReportSection title="Pickleball" icon={<Target className="w-3.5 h-3.5 text-lime-400" />} items={report.sections.pickleball} />
          <ReportSection title="Gym / Fitness" icon={<TrendingUp className="w-3.5 h-3.5 text-cyan-400" />} items={report.sections.gym_fitness} />
          <ReportSection title="Cafe / Do uong" icon={<Star className="w-3.5 h-3.5 text-amber-400" />} items={report.sections.cafe_drinks} />
        </div>
      ) : null}
    </div>
  );
}

function ReportSection({ title, icon, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
      <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5">{icon} {title}</p>
      <div className="space-y-2">
        {items.map((t, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-rose-400 font-mono">{t.hashtag}</span>
              <span className="text-xs text-slate-200">{t.keyword}</span>
              {t.is_quick_win && <Badge className="text-[7px] border-0 bg-lime-500/20 text-lime-400">Quick</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500">+{t.growth_rate}%/w</span>
              <span className={`text-xs font-bold ${t.trend_score >= 70 ? "text-rose-400" : t.trend_score >= 50 ? "text-amber-400" : "text-slate-400"}`}>{t.trend_score}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== ALL TRENDS ====================
function AllTrendsTab() {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [videoIdea, setVideoIdea] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const p = catFilter ? `?category=${catFilter}` : "";
      const { data } = await api.get(`/tiktok-trends/trends${p}`);
      setTrends(data.trends || []);
    } catch { toast.error("Lỗi"); }
    finally { setLoading(false); }
  }, [catFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  const doAction = async (id, type) => {
    try { const { data } = await api.post(`/tiktok-trends/trends/${id}/action?action_type=${type}`); toast.success(data.message); } catch { toast.error("Lỗi"); }
  };

  const genVideo = async (id) => {
    setVideoLoading(true);
    try {
      const { data } = await api.post(`/tiktok-trends/generate-video-idea/${id}`);
      setVideoIdea(data.video_idea);
    } catch { toast.error("Lỗi"); }
    finally { setVideoLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="all-trends-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Flame className="w-5 h-5 text-rose-400" /> Tất cả Trends</h2>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="bg-[#1e293b] border border-[#334155] rounded-md px-2 py-1.5 text-xs text-slate-300">
          <option value="">Tat ca</option>
          {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? <Loader2 className="w-6 h-6 text-rose-400 animate-spin mx-auto mt-8" /> :
      trends.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có trend. Bam "AI Phân tích" trong tab Báo cáo.</p> :
      <div className="space-y-3">
        {trends.map(t => (
          <TrendCard key={t.id} trend={t} onAction={doAction} onDetail={setDetail} onGenVideo={genVideo} />
        ))}
      </div>}

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-lg max-h-[85vh] overflow-y-auto">
          {detail && <TrendDetail trend={detail} onAction={doAction} onGenVideo={genVideo} />}
        </DialogContent>
      </Dialog>

      {/* Video Idea Dialog */}
      <Dialog open={!!videoIdea} onOpenChange={() => setVideoIdea(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-lg max-h-[85vh] overflow-y-auto">
          {videoIdea && <VideoIdeaView idea={videoIdea} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TrendCard({ trend, onAction, onDetail, onGenVideo }) {
  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 hover:border-rose-500/20 transition-colors" data-testid={`trend-${trend.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 cursor-pointer" onClick={() => onDetail(trend)}>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] text-rose-400 font-mono">{trend.hashtag}</span>
            <Badge className={`text-[8px] border-0 ${CAT_COLORS[trend.category] || ""}`}>{CAT_LABELS[trend.category] || trend.category}</Badge>
            {trend.is_quick_win && <Badge className="text-[7px] border-0 bg-lime-500/20 text-lime-400">Quick Win</Badge>}
            {trend.region && <span className="text-[9px] text-slate-600"><MapPin className="w-2.5 h-2.5 inline" /> {trend.region}</span>}
          </div>
          <h3 className="text-sm font-medium text-slate-200">{trend.keyword}</h3>
          <p className="text-xs text-slate-400 mt-0.5">{trend.trend_summary}</p>
        </div>
        <div className="flex flex-col items-end shrink-0 ml-3">
          <span className={`text-lg font-bold ${trend.trend_score >= 70 ? "text-rose-400" : trend.trend_score >= 50 ? "text-amber-400" : "text-slate-400"}`}>{trend.trend_score}</span>
          <span className="text-[8px] text-slate-600">trend</span>
          {trend.karinox_fit > 0 && <span className="text-[9px] text-cyan-400 mt-0.5">Fit: {trend.karinox_fit}</span>}
        </div>
      </div>

      {/* Mini scores */}
      <div className="flex gap-2 mb-2 text-[9px] text-slate-500 flex-wrap">
        <span>Viral: <span className="text-rose-400">{trend.viral_score}</span></span>
        <span>Growth: <span className="text-lime-400">+{trend.growth_rate}%</span></span>
        <span>Engage: <span className="text-cyan-400">{trend.engagement_rate}%</span></span>
        {trend.music_name && <span className="text-purple-400"><Music className="w-2.5 h-2.5 inline" /> {trend.music_name.slice(0, 20)}</span>}
      </div>

      {/* Hooks preview */}
      {trend.hooks?.length > 0 && (
        <p className="text-[10px] text-amber-400/80 mb-2 italic">"{trend.hooks[0]}"</p>
      )}

      {/* Actions */}
      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => onGenVideo(trend.id)} className="text-[9px] text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded hover:bg-rose-500/20"><Video className="w-3 h-3 inline mr-0.5" />Kịch bản</button>
        <button onClick={() => onAction(trend.id, "content_plan")} className="text-[9px] text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded hover:bg-cyan-500/20"><Play className="w-3 h-3 inline mr-0.5" />KH quay</button>
        <button onClick={() => onAction(trend.id, "experiment")} className="text-[9px] text-lime-400 bg-lime-500/10 px-2 py-0.5 rounded hover:bg-lime-500/20"><Target className="w-3 h-3 inline mr-0.5" />Thử nghiệm</button>
        <button onClick={() => onAction(trend.id, "calendar")} className="text-[9px] text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded hover:bg-purple-500/20"><Calendar className="w-3 h-3 inline mr-0.5" />Lịch content</button>
      </div>
    </div>
  );
}

function TrendDetail({ trend, onAction, onGenVideo }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-rose-400 flex items-center gap-2"><Flame className="w-5 h-5" /> {trend.keyword}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-rose-400 font-mono">{trend.hashtag}</span>
          <Badge className={`text-[8px] border-0 ${CAT_COLORS[trend.category]}`}>{CAT_LABELS[trend.category]}</Badge>
          {trend.is_quick_win && <Badge className="text-[8px] border-0 bg-lime-500/20 text-lime-400">Quick Win</Badge>}
          {trend.should_do && <Badge className="text-[8px] border-0 bg-emerald-500/20 text-emerald-400">Nên làm</Badge>}
        </div>

        <p className="text-xs text-slate-300">{trend.trend_summary}</p>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-rose-400 uppercase">Vì sao hot ở VN</p><p className="text-[10px] text-slate-300 mt-0.5">{trend.why_hot_vietnam}</p></div>
          <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-cyan-400 uppercase">Áp dụng Karinox</p><p className="text-[10px] text-slate-300 mt-0.5">{trend.karinox_application}</p></div>
          <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-500 uppercase">KH mục tiêu</p><p className="text-[10px] text-slate-300 mt-0.5">{trend.target_audience}</p></div>
          <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-500 uppercase">Làm thế nào</p><p className="text-[10px] text-slate-300 mt-0.5">{trend.how_to_do}</p></div>
        </div>

        {/* Scores */}
        <div className="bg-[#0f172a] rounded-lg p-3">
          <p className="text-[9px] text-slate-500 uppercase mb-2">Điểm số</p>
          <div className="grid grid-cols-4 gap-2 text-center text-[10px]">
            <div><p className="text-slate-600">Viral</p><p className="font-bold text-rose-400">{trend.viral_score}</p></div>
            <div><p className="text-slate-600">Growth</p><p className="font-bold text-lime-400">{trend.growth_score}</p></div>
            <div><p className="text-slate-600">Engage</p><p className="font-bold text-cyan-400">{trend.engagement_score}</p></div>
            <div><p className="text-slate-600">Biz Fit</p><p className="font-bold text-amber-400">{trend.business_fit_score}</p></div>
            <div><p className="text-slate-600">VN Fit</p><p className="font-bold text-purple-400">{trend.local_fit_score}</p></div>
            <div><p className="text-slate-600">Relevant</p><p className="font-bold text-sky-400">{trend.relevance_score}</p></div>
            <div><p className="text-slate-600">Kho</p><p className="font-bold text-slate-300">{trend.difficulty_score}</p></div>
            <div><p className="text-slate-600">Karinox</p><p className="font-bold text-rose-400">{trend.karinox_fit}</p></div>
          </div>
        </div>

        {/* Hooks */}
        {trend.hooks?.length > 0 && (
          <div className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-3">
            <p className="text-[9px] text-rose-400 uppercase mb-1">Hooks TikTok VN</p>
            {trend.hooks.map((h, i) => <p key={i} className="text-[10px] text-slate-300 italic">"{h}"</p>)}
          </div>
        )}

        {/* Content angles */}
        {trend.content_angles?.length > 0 && (
          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
            <p className="text-[9px] text-cyan-400 uppercase mb-1">Góc nội dung</p>
            {trend.content_angles.map((a, i) => <p key={i} className="text-[10px] text-slate-300">• {a}</p>)}
          </div>
        )}

        {trend.script_idea && (
          <div className="bg-[#0f172a] rounded-lg p-3">
            <p className="text-[9px] text-amber-400 uppercase mb-1">Kịch bản</p>
            <p className="text-[10px] text-slate-300">{trend.script_idea}</p>
          </div>
        )}

        {trend.music_name && <p className="text-[10px] text-purple-400"><Music className="w-3 h-3 inline mr-1" />{trend.music_name}</p>}
        {trend.cta && <p className="text-[10px] text-lime-400">CTA: {trend.cta}</p>}

        <div className="flex gap-1.5 pt-2 border-t border-[#334155]">
          <Button onClick={() => onGenVideo(trend.id)} size="sm" className="bg-rose-500/10 text-rose-400 border border-rose-500/20 h-7 text-[10px] gap-1"><Video className="w-3 h-3" /> Tạo kịch bản</Button>
          <Button onClick={() => onAction(trend.id, "experiment")} size="sm" className="bg-lime-500/10 text-lime-400 border border-lime-500/20 h-7 text-[10px] gap-1"><Target className="w-3 h-3" /> Thử nghiệm</Button>
          <Button onClick={() => onAction(trend.id, "calendar")} size="sm" className="bg-purple-500/10 text-purple-400 border border-purple-500/20 h-7 text-[10px] gap-1"><Calendar className="w-3 h-3" /> Lịch content</Button>
        </div>
      </div>
    </>
  );
}

function VideoIdeaView({ idea }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-rose-400 flex items-center gap-2"><Video className="w-5 h-5" /> {idea.title}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        {idea.hooks?.length > 0 && (
          <div className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-3">
            <p className="text-[9px] text-rose-400 uppercase mb-1">Hooks</p>
            {idea.hooks.map((h, i) => <p key={i} className="text-[10px] text-slate-300 italic">"{h}"</p>)}
          </div>
        )}
        {idea.content_angles?.length > 0 && (
          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
            <p className="text-[9px] text-cyan-400 uppercase mb-1">Góc nội dung</p>
            {idea.content_angles.map((a, i) => <p key={i} className="text-[10px] text-slate-300">• {a}</p>)}
          </div>
        )}
        <div className="bg-[#0f172a] rounded-lg p-3">
          <p className="text-[9px] text-amber-400 uppercase mb-1">Kịch bản chi tiet</p>
          <p className="text-[10px] text-slate-300 whitespace-pre-wrap">{idea.script}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {idea.duration && <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-600">Thời lượng</p><p className="text-[10px] text-slate-300">{idea.duration}</p></div>}
          {idea.location && <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-600">Địa điểm</p><p className="text-[10px] text-slate-300">{idea.location}</p></div>}
          {idea.talent_needed && <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-600">Người đóng</p><p className="text-[10px] text-slate-300">{idea.talent_needed}</p></div>}
          {idea.best_post_time && <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-600">Giờ đăng</p><p className="text-[10px] text-slate-300">{idea.best_post_time}</p></div>}
        </div>
        {idea.music_suggestion && <p className="text-[10px] text-purple-400"><Music className="w-3 h-3 inline mr-1" />{idea.music_suggestion}</p>}
        {idea.filming_tips?.length > 0 && (
          <div className="bg-[#0f172a] rounded-lg p-3">
            <p className="text-[9px] text-slate-500 uppercase mb-1">Tips quay</p>
            {idea.filming_tips.map((t, i) => <p key={i} className="text-[10px] text-slate-300">• {t}</p>)}
          </div>
        )}
        {idea.equipment_needed?.length > 0 && <p className="text-[10px] text-slate-500">Thiết bị: {idea.equipment_needed.join(", ")}</p>}
        {idea.hashtags?.length > 0 && <p className="text-[10px] text-rose-400">Hashtags: {idea.hashtags.join(" ")}</p>}
        {idea.cta && <p className="text-[10px] text-lime-400 font-medium">CTA: {idea.cta}</p>}
        {idea.expected_result && <p className="text-[10px] text-slate-500">KQ kỳ vọng: {idea.expected_result}</p>}
      </div>
    </>
  );
}

// ==================== QUICK WIN ====================
function QuickWinTab() {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tiktok-trends/trends?quick_win=true").then(r => { setTrends(r.data.trends || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const doAction = async (id, type) => {
    try { const { data } = await api.post(`/tiktok-trends/trends/${id}/action?action_type=${type}`); toast.success(data.message); } catch { toast.error("Lỗi"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-rose-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="quick-win-tab">
      <div>
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Zap className="w-5 h-5 text-lime-400" /> Quick Win — Dễ làm ngay</h2>
        <p className="text-[10px] text-slate-500 mt-0.5">Trends dễ quay, ít chi phí, có thể ra video trong 1-2 ngày</p>
      </div>

      {trends.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có Quick Win. Tạo trends trước.</p> :
      <div className="space-y-3">
        {trends.map(t => (
          <div key={t.id} className="bg-lime-500/5 border border-lime-500/20 rounded-xl p-4" data-testid={`qw-${t.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <Badge className="text-[7px] border-0 bg-lime-500/20 text-lime-400">Quick Win</Badge>
                  <span className="text-[10px] text-rose-400 font-mono">{t.hashtag}</span>
                  <Badge className={`text-[8px] border-0 ${CAT_COLORS[t.category]}`}>{CAT_LABELS[t.category]}</Badge>
                </div>
                <h3 className="text-sm font-medium text-slate-200">{t.keyword}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{t.trend_summary}</p>
              </div>
              <span className="text-lg font-bold text-lime-400">{t.trend_score}</span>
            </div>
            {t.hooks?.[0] && <p className="text-[10px] text-amber-400/80 italic mb-2">"{t.hooks[0]}"</p>}
            <p className="text-[10px] text-slate-500 mb-2">{t.how_to_do}</p>
            <div className="flex gap-1.5">
              <button onClick={() => doAction(t.id, "script")} className="text-[9px] text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded">Kịch bản</button>
              <button onClick={() => doAction(t.id, "content_plan")} className="text-[9px] text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">KH quay</button>
              <button onClick={() => doAction(t.id, "calendar")} className="text-[9px] text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded">Lịch content</button>
            </div>
          </div>
        ))}
      </div>}
    </div>
  );
}

// ==================== CATEGORY TRENDS ====================
function CategoryTrendsTab({ category }) {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/tiktok-trends/trends?category=${category}`).then(r => { setTrends(r.data.trends || []); setLoading(false); }).catch(() => setLoading(false));
  }, [category]);

  const doAction = async (id, type) => {
    try { const { data } = await api.post(`/tiktok-trends/trends/${id}/action?action_type=${type}`); toast.success(data.message); } catch { toast.error("Lỗi"); }
  };

  const catTitle = CAT_LABELS[category] || category;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-rose-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid={`cat-${category}-tab`}>
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2">
        <Target className="w-5 h-5 text-rose-400" /> Trends: {catTitle}
      </h2>

      {trends.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có trend cho {catTitle}. Tạo trends từ tab Báo cáo.</p> :
      <div className="space-y-3">
        {trends.map(t => (
          <TrendCard key={t.id} trend={t} onAction={doAction} onDetail={() => {}} onGenVideo={() => {}} />
        ))}
      </div>}
    </div>
  );
}
